//#region imports
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DateRangePicker } from '@tremor/react';
import ECACReport from './ecac-report';
import ExternalCallPointReport from './external-call-point-report';
import MovementReport from './movement-report';
import MovementSheetReport from './movement-sheet-report';
import BoardingSlaReport from './boarding-sla-report';
import TransferSlaReport from './transfer-sla-report';
import InboundSlaReport from './inbound-sla-report';
import TerminalReceptionReport from './terminal-reception-report';
import FlightAuditReport from './flight-audit-report';
import BfsArrivalAuditReport from './bfs-arrival-audit-report';
import EmployeeLocationAuditReport from './employee-location-audit-report';
import AirlinePerformanceReport from './airline-performance-report';
import EcacDepartureSummaryReport from './ecac-departure-summary-report';
import EcacArrivalSummaryReport from './ecac-arrival-summary-report';
import SlaSummaryReport from './sla-summary-report';
import CorkSlaReport from './cork-sla-report';
import CorkScorecardReport from './cork-scorecard-report';
import BfsSlaReport from './bfs-sla-report';
import DublinScorecardReport from './dublin-scorecard-report';
import ArrivalTasksReport from './arrival-tasks-report';
import { selectUserDb } from '_redux/slices/main-screen/user';
import { CustomDropdown } from '_atoms';
//#endregion

const SINGLE_DATE_REPORTS = [];

//#region report options
const ALL_REPORT_OPTIONS = [
    { label: 'Terminal Reception', value: 'ecac' },
    { label: 'External Call Point', value: 'external-call-point' },
    { label: 'Boardings', value: 'boarding-sla' },
    { label: 'Transfer SLA', value: 'transfer-sla' },
    { label: 'Inbounds', value: 'inbound-sla' },
    { label: 'Terminal Holding Points (218/407)', value: 'terminal-reception' },
    { label: 'Airline Performance', value: 'airline-performance' },
    { label: 'ECAC Departure Summary', value: 'ecac-departure-summary' },
    { label: 'ECAC Arrival Summary', value: 'ecac-arrival-summary' },
    { label: 'Flight Audit Report', value: 'flight-audit' },
    { label: 'Employee Location Audit', value: 'employee-location-audit' },
    { label: 'Movement Report', value: 'movement' },
    { label: 'Movement Sheet', value: 'movement-sheet' },
    { label: 'SLA Summary Report', value: 'sla-summary' },
    { label: 'Cork SLA', value: 'cork-sla', airports: ['ORK'] },
    { label: 'Cork Scorecard', value: 'cork-scorecard', airports: ['ORK'] },
    { label: 'BFS SLA', value: 'bfs-sla', airports: ['BFS'] },
    { label: 'BFS Arrivals Audit', value: 'bfs-arrival-audit', airports: ['BFS'] },
    { label: 'Dublin Scorecard', value: 'dublin-scorecard', airports: ['DUB'] },
    { label: 'Arrival Tasks', value: 'arrival-tasks', airports: ['DUB'] },
];
//#endregion

const runReportButtonStyle = {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#3a82f6',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

const BFS_REPORT_VALUES = ['airline-performance', 'ecac-departure-summary', 'ecac-arrival-summary', 'movement', 'flight-audit', 'employee-location-audit', 'sla-summary', 'bfs-sla', 'bfs-arrival-audit'];
const ORK_REPORT_VALUES = ['ecac-departure-summary', 'ecac-arrival-summary', 'airline-performance', 'movement', 'cork-sla', 'cork-scorecard'];
const SNN_REPORT_VALUES = ['ecac-departure-summary', 'ecac-arrival-summary', 'airline-performance', 'movement', 'movement-sheet'];
const PUJ_REPORT_VALUES = ['movement'];
const REPORTS_ONLY_HIDDEN_VALUES = ['flight-audit', 'employee-location-audit'];
const OWN_DATE_FILTER_REPORTS = ['flight-audit', 'employee-location-audit', 'bfs-arrival-audit'];

const SlaReport = ({ externalKPIData, dateRange, setDateRange, iata, onReportChange }) => {
    const userDb = useSelector(selectUserDb);
    const userRole = userDb?.role;
    const userAuthority = userDb?.details?.authority;
    const hideBreakdown = userRole === 'REPORTS_ONLY';
    const runReportRef = useRef(null);
    const [runLoading, setRunLoading] = useState(false);
    const [canRunReport, setCanRunReport] = useState(true);

    const reportOptions = useMemo(() => {
        let opts = ALL_REPORT_OPTIONS.filter(
            (opt) => !opt.roleRequired || opt.roleRequired === userRole
        );
        opts = opts.filter((opt) => !opt.airports || opt.airports.includes(iata));
        const hasAirportAuthority = userAuthority === 'BFS' || userAuthority === 'ORK' || userAuthority === 'SNN' || userAuthority === 'PUJ';
        if (userAuthority === 'BFS') {
            opts = opts.filter((opt) => BFS_REPORT_VALUES.includes(opt.value));
        } else if (userAuthority === 'ORK') {
            opts = opts.filter((opt) => ORK_REPORT_VALUES.includes(opt.value));
        } else if (userAuthority === 'SNN') {
            opts = opts.filter((opt) => SNN_REPORT_VALUES.includes(opt.value));
        } else if (userAuthority === 'PUJ') {
            opts = opts.filter((opt) => PUJ_REPORT_VALUES.includes(opt.value));
        }
        if (userRole === 'REPORTS_ONLY' && !hasAirportAuthority) {
            opts = opts.filter((opt) => !REPORTS_ONLY_HIDDEN_VALUES.includes(opt.value));
        }
        return opts.map((opt) => ({ label: opt.label, value: opt.value }));
    }, [userRole, userAuthority, iata]);

    const defaultReport = (userRole === 'REPORTS_ONLY' && reportOptions.some((o) => o.value === 'cork-sla'))
        ? 'cork-sla'
        : reportOptions.some((o) => o.value === 'ecac')
        ? 'ecac'
        : reportOptions.some((o) => o.value === 'ecac-departure-summary')
        ? 'ecac-departure-summary'
        : reportOptions.some((o) => o.value === 'flight-audit')
        ? 'flight-audit'
        : reportOptions.some((o) => o.value === 'movement')
        ? 'movement'
        : (reportOptions[0]?.value ?? null);
    const [selectedReport, setSelectedReport] = useState(defaultReport);

    // Reset selection if current selection is no longer available (e.g. role changed)
    const effectiveReport = reportOptions.some((o) => o.value === selectedReport)
        ? selectedReport
        : defaultReport;

    const selectedOption = reportOptions.find((o) => o.value === effectiveReport) || reportOptions[0];

    useEffect(() => {
        if (onReportChange) onReportChange(effectiveReport);
    }, [effectiveReport, onReportChange]);

    useEffect(() => {
        setRunLoading(false);
        setCanRunReport(true);
    }, [effectiveReport]);

    const handleReportChange = (selection) => {
        if (selection?.value) setSelectedReport(selection.value);
    };

    const singleDateOnly = SINGLE_DATE_REPORTS.includes(effectiveReport);

    return (
        <div style={{ marginBottom: 300 }}>            

            {/* report dropdown | date picker | run button - all on one line (sticky) */}
            <div style={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                marginBottom: '48px',
                paddingTop: '8px',
                paddingBottom: '24px',
                flexWrap: 'nowrap',
                gap: '24px',
                backgroundColor: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}>
                {/* left: report dropdown */}
                {reportOptions.length > 0 ? (
                    <div style={{ width: '280px', flexShrink: 0 }}>
                        <CustomDropdown
                            name="sla-report-select"
                            title="Report Type"
                            value={selectedOption}
                            onChange={handleReportChange}
                            options={reportOptions}
                        />
                    </div>
                ) : (
                    <div style={{ width: '280px', flexShrink: 0 }}>
                        <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                            No reports available for your role.
                        </p>
                    </div>
                )}
                {/* center: date picker - hidden for reports with their own date/time filters */}
                {setDateRange && !OWN_DATE_FILTER_REPORTS.includes(effectiveReport) && (
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                    }}>
                        {singleDateOnly ? (
                            <input
                                type="date"
                                value={dateRange?.[0] ? new Date(dateRange[0]).toISOString().split('T')[0] : ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val) {
                                        const d = new Date(val + 'T12:00:00');
                                        setDateRange([d, d]);
                                    }
                                }}
                                style={{
                                    height: '38px',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #d1d5db',
                                    fontSize: '14px',
                                    width: '220px',
                                    boxSizing: 'border-box',
                                }}
                            />
                        ) : (
                            <DateRangePicker
                                defaultValue={dateRange}
                                disabled={false}
                                onValueChange={(value) => setDateRange(value)}
                                value={dateRange}
                                enableDropdown={true}
                                maxWidth="max-w-lg"
                                minWidth="max-w-lg"
                                dropdownPlaceholder={'Today'}
                            />
                        )}
                    </div>
                )}
                {/* right: run report button */}
                <div style={{ width: '140px', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={() => runReportRef.current?.()}
                        disabled={runLoading || !canRunReport}
                        style={{
                            ...runReportButtonStyle,
                            backgroundColor: (runLoading || !canRunReport) ? '#93c5fd' : '#3a82f6',
                            cursor: (runLoading || !canRunReport) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {runLoading ? 'Running...' : 'Run Report'}
                    </button>
                </div>
            </div>

            {/* ecac report */}
            {effectiveReport === 'ecac' && (
                <ECACReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} hideBreakdown={hideBreakdown} />
            )}

            {/* external call point report */}
            {effectiveReport === 'external-call-point' && (
                <ExternalCallPointReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} hideBreakdown={hideBreakdown} />
            )}

            {/* movement report */}
            {effectiveReport === 'movement' && (
                <MovementReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} hideBreakdown={hideBreakdown} />
            )}

            {/* movement sheet report */}
            {effectiveReport === 'movement-sheet' && (
                <MovementSheetReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} hideBreakdown={hideBreakdown} />
            )}

            {/* boarding sla report */}
            {effectiveReport === 'boarding-sla' && (
                <BoardingSlaReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} hideBreakdown={hideBreakdown} />
            )}

            {/* transfer sla report */}
            {effectiveReport === 'transfer-sla' && (
                <TransferSlaReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} hideBreakdown={hideBreakdown} />
            )}

            {/* inbound sla report */}
            {effectiveReport === 'inbound-sla' && (
                <InboundSlaReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} hideBreakdown={hideBreakdown} />
            )}

            {/* terminal reception report */}
            {effectiveReport === 'terminal-reception' && (
                <TerminalReceptionReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} hideBreakdown={hideBreakdown} />
            )}

            {/* flight audit report */}
            {effectiveReport === 'flight-audit' && (
                <FlightAuditReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} onCanRunChange={setCanRunReport} />
            )}

            {/* employee location audit report */}
            {effectiveReport === 'bfs-arrival-audit' && (
                <BfsArrivalAuditReport iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} onCanRunChange={setCanRunReport} />
            )}

            {effectiveReport === 'employee-location-audit' && (
                <EmployeeLocationAuditReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} onCanRunChange={setCanRunReport} />
            )}

            {/* airline performance report */}
            {effectiveReport === 'airline-performance' && (
                <AirlinePerformanceReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} hideBreakdown={hideBreakdown} />
            )}

            {/* ecac departure summary report */}
            {effectiveReport === 'ecac-departure-summary' && (
                <EcacDepartureSummaryReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} hideBreakdown={hideBreakdown} />
            )}

            {/* ecac arrival summary report */}
            {effectiveReport === 'ecac-arrival-summary' && (
                <EcacArrivalSummaryReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} hideBreakdown={hideBreakdown} />
            )}

            {/* sla summary report */}
            {effectiveReport === 'sla-summary' && (
                <SlaSummaryReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} />
            )}

            {/* cork sla report */}
            {effectiveReport === 'cork-sla' && (
                <CorkSlaReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} />
            )}

            {/* cork scorecard report */}
            {effectiveReport === 'cork-scorecard' && (
                <CorkScorecardReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} />
            )}

            {/* bfs sla report */}
            {effectiveReport === 'bfs-sla' && (
                <BfsSlaReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} />
            )}

            {/* dublin scorecard report */}
            {effectiveReport === 'dublin-scorecard' && (
                <DublinScorecardReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} />
            )}

            {/* arrival tasks report */}
            {effectiveReport === 'arrival-tasks' && (
                <ArrivalTasksReport dateRange={dateRange} iata={iata} runReportRef={runReportRef} onRunLoadingChange={setRunLoading} />
            )}
        </div>
    );
};

export default SlaReport;